// @ts-nocheck
// Turns a launch's job log into an on-chain checklist: each action as a row with
// ✓/✗ and a Solscan link — every claim verifiable on-chain.
import { ExternalLink } from "lucide-react";
import type { LaunchLogEntry } from "@/lib/algo/apiLive";

const tx = (s?: unknown) => (typeof s === "string" && s ? `https://solscan.io/tx/${s}` : undefined);

type Row = { ok: boolean | null; label: string; href?: string; detail?: string };

function rowsFromLog(log: LaunchLogEntry[], running: boolean): Row[] {
  const rows: Row[] = [];
  const ld = log.find((e) => e.kind === "launch-done");
  const ls = log.find((e) => e.kind === "launch-start");
  if (ld) {
    rows.push({ ok: true, label: `Token created${ls?.devBuy ? ` + dev buy ${ls.devBuy} SOL` : ""}`, href: tx(ld.signature), detail: typeof ld.mint === "string" ? ld.mint.slice(0, 8) + "…" : undefined });
    if (ld.rewardSignature) rows.push({ ok: true, label: "Creator reward configured", href: tx(ld.rewardSignature) });
  } else if (running) {
    rows.push({ ok: null, label: "Creating token…" });
  }
  for (const e of log) {
    if (e.kind === "buy") rows.push({ ok: true, label: `${e.wallet} bought ${e.sol} SOL`, href: tx(e.signature) });
    else if (e.kind === "rotate") rows.push({ ok: true, label: `${e.from} → ${e.to} rotated`, href: tx(e.buySig) ?? tx(e.sellSig) });
    else if (e.kind === "rotate-skipped") rows.push({ ok: false, label: `${e.from} → ${e.to} rotation skipped`, detail: String(e.reason ?? "") });
    else if (e.kind === "timed-sell") rows.push({ ok: true, label: `${e.wallet} sold ${e.pct}%${e.value ? ` @ ${e.value}s` : ""}`, href: tx(e.signature), detail: e.solOut ? `+${Number(e.solOut).toFixed(4)} SOL` : undefined });
    else if (e.kind === "sell-skipped") rows.push({ ok: false, label: `${e.wallet} sell skipped`, detail: String(e.reason ?? "") });
    else if (e.kind === "error") rows.push({ ok: false, label: `${e.action}${e.wallet ? ` · ${e.wallet}` : ""} failed`, detail: String(e.message ?? "") });
  }
  return rows;
}

export function LaunchChecklist({ log, mint, running, ok }: { log: LaunchLogEntry[]; mint?: string; running?: boolean; ok?: boolean | null }) {
  const rows = rowsFromLog(log, !!running);
  return (
    <div className="checklist">
      <div className="checklist-head">
        <span className={`live-tag ${ok === false ? "off" : ok ? "on" : ""}`}>{running ? "RUNNING" : ok === false ? "HALTED" : ok ? "LAUNCHED" : "—"}</span>
        {mint && <a className="small" href={`https://solscan.io/token/${mint}`} target="_blank" rel="noreferrer">{mint.slice(0, 8)}… <ExternalLink size={11} /></a>}
        <span className="small muted">{rows.filter((r) => r.ok).length}/{rows.length} on-chain</span>
      </div>
      {rows.length === 0 && <div className="small muted" style={{ padding: "8px 0" }}>No actions yet.</div>}
      {rows.map((r, i) => (
        <div key={i} className={`cl-row ${r.ok === false ? "bad" : r.ok ? "ok" : "pending"}`}>
          <span className="cl-icon">{r.ok === false ? "✗" : r.ok ? "✓" : "⋯"}</span>
          <span className="cl-label">{r.label}</span>
          {r.detail && <span className="cl-detail">{r.detail}</span>}
          {r.href && <a href={r.href} target="_blank" rel="noreferrer" title="View on Solscan"><ExternalLink size={11} /></a>}
        </div>
      ))}
    </div>
  );
}
